import {
  IonAvatar,
  IonButton,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonLoading,
  IonRow,
  IonText,
  useIonAlert,
} from "@ionic/react";
import { send } from "ionicons/icons";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { decodeParam } from "../util/ApiHelper";
import "./Remarks.css";

import { appInsights, reactPlugin } from "../util/AppInsights";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useGetComments from "../hooks/work-order/useGetComments";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import useSubmitNewComments from "../hooks/work-order/useSubmitNewComments";
import { useOfflineStore } from "../store/offline-mode";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";
import { RemarksCollection } from "../models/remarks/RemarksCollection";
import { formatCommentTimestamp } from "../util/DateHelper";

const RemarksTab: React.FC = () => {
  appInsights?.trackPageView({ name: "RemarksTab" });
  const { wOrderDate, wOrderTime, workOrderId } = useParams<{
    wOrderDate: string;
    wOrderTime: string;
    workOrderId: string;
  }>();
  let displayWo = decodeParam(workOrderId);
  let wDate = decodeParam(wOrderDate);
  let wTime = decodeParam(wOrderTime);
  let commentField: HTMLInputElement = document.getElementById(
    "comment-input"
  ) as HTMLInputElement;
  const [presentAlert] = useIonAlert();

  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [remarksErrorState, setRemarksErrorState] = useState(false);

  const [showFetching, setShowFetching] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const { submitNewComments } = useSubmitNewComments();

  const comments = useWorkOrderStore(
    useShallow((state) => state.getCommentsForWo(displayWo))
  );
  const addOfflineComments = useWoOfflineUpdatesStore(
    (state) => state.addOfflineComments
  );
  const isOffline = useOfflineStore((state) => state.isOffline);
  const { getComments: triggerGetComments } = useGetComments();
  const remarks = comments ? RemarksCollection.fromString(comments) : [];
  const hasRemarks = !!remarks?.length;

  async function submitNewCommentClick() {
    if (isOffline) {
      addOfflineComments({
        comment: commentField.value,
        woNumber: displayWo,
        commentDate: formatCommentTimestamp(),
      });
      commentField.value = "";
      presentAlert({
        message:
          translatedMessages["MobOfflineCommentAdded"]?.MessageText ||
          "Comment added in offline store.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      return; //we will sync this once user is back online
    }
    setShowFetching(true);
    setIsSubmitDisabled(true);
    const response = await submitNewComments(commentField.value, displayWo);
    if (response?.status !== 200) {
      setRemarksErrorState(true);
    } else {
      commentField.value = "";
      getComments(true);
    }
    setShowFetching(false);
  }

  function onCommentChange(event: any) {
    setIsSubmitDisabled(true);
    const input = event.target as HTMLInputElement;
    if (input.value.length > 0) {
      setIsSubmitDisabled(false);
    } else {
      setIsSubmitDisabled(true);
    }
  }

  const getComments = useCallback(
    async (refetch: boolean = false) => {
      if (comments !== undefined && !refetch) return; //got it from cache
      try {
        const response = await triggerGetComments(displayWo, wDate, wTime);
        if (response.status !== 200) {
          setRemarksErrorState(true);
        } else {
          setRemarksErrorState(false);
        }
      } catch (error) {
        appInsights?.trackEvent({
          name:
            translatedMessages["ErrRetrievingComment"]?.MessageText ||
            "Error retrieving Comments information",
          properties: {
            error: error,
            wDate: wDate,
            wTime: wTime,
            comment: "true",
            completion: "true",
          },
        });
      }
    },
    [comments, displayWo, translatedMessages, triggerGetComments, wDate, wTime]
  );

  useEffect(() => {
    (async () => {
      setShowFetching(true);

      await getComments();
      setShowFetching(false);
    })();
  }, [getComments]);

  return (
    <>
      <IonLoading
        isOpen={showFetching}
        onDidDismiss={() => setShowFetching(false)}
        duration={5000}
      />
      <IonGrid class="comments-ion-grid">
        <IonRow class="comment-add-new">
          <IonCol>
            <IonInput
              id="comment-input"
              placeholder={translations["lbl_add_comment"] || "Add a comment"}
              onIonChange={onCommentChange}
              clearInput
            ></IonInput>
          </IonCol>
          <IonCol size="auto">
            <IonButton
              id="comment-btn"
              disabled={isSubmitDisabled}
              fill="clear"
              onClick={submitNewCommentClick}
            >
              <IonIcon
                id="send-icon"
                slot="icon-only"
                size="large"
                icon={send}
              ></IonIcon>
            </IonButton>
          </IonCol>
        </IonRow>
        {hasRemarks ? (
          remarks.map((item, index) => {
            return (
              <>
                <IonRow key={index} class="comment-listing">
                  <IonCol size="auto">
                    <IonAvatar class="remarks-avatar">
                      <span>{item.user?.charAt(0).toUpperCase()}</span>
                    </IonAvatar>
                  </IonCol>
                  <IonCol>
                    <IonGrid>
                      <IonRow>
                        <IonCol size="auto" class="comment-listing-user">
                          {item.user}
                        </IonCol>
                        <IonCol size="auto" class="ion-text-right">
                          {item.date}
                        </IonCol>
                      </IonRow>
                      <IonRow>
                        <IonCol size="auto" class="comment-listing-comment">
                          {item.comment}
                        </IonCol>
                      </IonRow>
                    </IonGrid>
                  </IonCol>
                </IonRow>
              </>
            );
          })
        ) : (
          <IonText
            color={remarksErrorState ? "danger" : "default"}
            style={{ padding: "16px", display: "inline-block" }}
            hidden={showFetching}
          >
            {remarksErrorState
              ? `${
                  translatedMessages["ErrRetrievingComment"]?.MessageText ||
                  "Error retrieving Comments information"
                }`
              : `${
                  translatedMessages["NoResultsFound"]?.MessageText ||
                  "No results found."
                }`}
          </IonText>
        )}
      </IonGrid>
    </>
  );
};

export default withAITracking(reactPlugin, RemarksTab, "RemarksTab");
